import { chatThreadsService } from "@/api/chat";
import { Button } from "@/components/ui/Button";
import { CHAT_ACCEPT } from "@/lib/uploadPolicy";
import { Textarea } from "@/components/ui/Textarea";
import { useChatStore } from "@/store/chat/chat.store";
import { TMessageFile } from "@/types/chat";
import { extractErrorMessage } from "@/utils/errorUtils";
import { Loader2, Paperclip, Send, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type TFileUploadState = {
  file: File;
  progress: number; // 0-100
  status: "pending" | "uploading" | "success" | "error";
  uploadedFile?: TMessageFile;
  error?: string;
};

type TMessageInputProps = {
  onSend?: (message: string, files?: TMessageFile[]) => void;
  disabled?: boolean;
  threadId?: string;
  companyId?: string;
  placeholder?: string;
};

export default function MessageInput({
  onSend,
  disabled,
  threadId,
  companyId,
  placeholder = "Type here...",
}: TMessageInputProps = {}) {
  const [message, setMessage] = useState("");
  const [fileUploads, setFileUploads] = useState<TFileUploadState[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const { activeChatId, addMessage } = useChatStore();

  // Upload files when they're selected (for thread chats)
  useEffect(() => {
    // For thread chats, we need onSend and threadId
    if (!onSend || !threadId) {
      return;
    }

    const pendingUploads = fileUploads.filter((upload) => upload.status === "pending");

    if (pendingUploads.length === 0) {
      return;
    }

    const uploadFiles = async () => {
      for (const upload of pendingUploads) {
        setFileUploads((prev) =>
          prev.map((u) => (u.file === upload.file ? { ...u, status: "uploading", progress: 0 } : u))
        );

        try {
          // Use thread chat upload service
          const { data, error } = await chatThreadsService.uploadThreadFile({
            threadId,
            file: upload.file,
            companyId,
          });

          if (error || !data) {
            const errorMessage = error ? await extractErrorMessage(error) : "Upload failed";
            setFileUploads((prev) =>
              prev.map((u) =>
                u.file === upload.file
                  ? {
                      ...u,
                      status: "error",
                      progress: 0,
                      error: errorMessage,
                    }
                  : u
              )
            );
            continue;
          }

          const fileId = data.data?.id ?? null;
          const fileUrl = data.data?.url;

          if (!fileId) {
            console.error("[chat-input] Upload response missing file ID", {
              threadId,
              companyId,
              fileName: upload.file.name,
            });
            setFileUploads((prev) =>
              prev.map((u) =>
                u.file === upload.file
                  ? {
                      ...u,
                      status: "error",
                      progress: 0,
                      error: "Upload succeeded but no file ID returned",
                    }
                  : u
              )
            );
          } else {
            setFileUploads((prev) =>
              prev.map((u) =>
                u.file === upload.file
                  ? {
                      ...u,
                      status: "success",
                      progress: 100,
                      uploadedFile: {
                        id: fileId,
                        name: upload.file.name,
                        url: fileUrl,
                      },
                    }
                  : u
              )
            );
          }
        } catch {
          setFileUploads((prev) =>
            prev.map((u) =>
              u.file === upload.file
                ? {
                    ...u,
                    status: "error",
                    progress: 0,
                    error: "Upload failed",
                  }
                : u
            )
          );
        }
      }
    };

    void uploadFiles();
  }, [fileUploads, onSend, threadId, companyId]);

  const handleSendMessage = () => {
    const hasContent = message.trim() || fileUploads.length > 0;
    const hasUploadingFiles = fileUploads.some(
      (upload) => upload.status === "uploading" || upload.status === "pending"
    );
    const hasSuccessfulUploads = fileUploads.some((upload) => upload.status === "success");

    if (disabled || !hasContent || hasUploadingFiles) {
      return;
    }

    // If onSend callback is provided (for thread chats), use it
    if (onSend) {
      // For thread chats, send the message text and uploaded files
      if (message.trim() || hasSuccessfulUploads) {
        const uploadedFiles = fileUploads
          .filter((upload) => upload.status === "success" && upload.uploadedFile?.id)
          .map((upload) => upload.uploadedFile!)
          .filter((file): file is TMessageFile => file.id != null && file.id !== "");

        onSend(message.trim(), uploadedFiles.length > 0 ? uploadedFiles : undefined);
        setMessage("");
        setFileUploads([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
      return;
    }

    // Default behavior for regular chats (store-based)
    if (activeChatId && hasContent) {
      // Convert File objects to MessageFile format
      const messageFiles: TMessageFile[] = fileUploads.map((upload) => ({
        id: Date.now().toString() + Math.random(),
        name: upload.file.name,
        // For initial implementation, we'll create blob URLs
        url: URL.createObjectURL(upload.file),
      }));

      addMessage(activeChatId, message.trim() || "(No text message)", messageFiles);
      setMessage("");
      setFileUploads([]);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Attachments are uploaded against a thread. On the new-chat screen there is no thread yet,
  // so a file selected here was silently marked "success", never uploaded, then dropped on send
  // — the founder saw "Uploaded successfully" and lost the file. Block the affordance instead.
  const canAttach = !onSend || Boolean(threadId);
  const attachHint = canAttach ? "Attach file" : "Send your first message, then attach files";

  const isUploading = fileUploads.some((upload) => upload.status === "uploading" || upload.status === "pending");
  const hasSuccessfulUploads = fileUploads.some((upload) => upload.status === "success");
  const canSend =
    disabled || isUploading
      ? false
      : onSend
        ? message.trim() || hasSuccessfulUploads
        : activeChatId && (message.trim() || fileUploads.length > 0);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Shift+Enter for new line, Enter to send
    if (e.key === "Enter" && !e.shiftKey && !disabled) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const processFiles = (files: File[]) => {
    if (!canAttach) {
      return;
    }

    const newUploads: TFileUploadState[] = files.map((file) => ({
      file,
      progress: 0,
      // Mark as pending if we need to upload (thread chats), otherwise success (regular chats)
      status: onSend && threadId ? "pending" : "success",
    }));
    setFileUploads((prev) => [...prev, ...newUploads]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    processFiles(files);
  };

  const handleRemoveFile = (fileToRemove: File) => {
    setFileUploads((prev) => prev.filter((upload) => upload.file !== fileToRemove));
  };

  const handleFileInputClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isUploading && canAttach) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone
    if (!dropZoneRef.current?.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled || isUploading) {
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFiles(files);
    }
  };

  return (
    <div
      ref={dropZoneRef}
      className={`relative px-4 pb-4 pt-3 transition-colors ${
        isDragging
          ? "rounded-2xl border-2 border-dashed border-primary/30 bg-primary/10"
          : "bg-muted"
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag and drop overlay */}
      {isDragging && (
        <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center rounded-2xl border-2 border-dashed border-primary bg-primary/10 bg-opacity-95">
          <div className="px-4 text-center">
            <Paperclip className="mx-auto mb-1.5 h-8 w-8 text-primary" />
            <p className="text-sm font-medium text-primary">Drop files here to upload</p>
          </div>
        </div>
      )}

      {/* File upload progress indicators - above the floating bar */}
      {fileUploads.length > 0 && (
        <div className="mb-3 space-y-2 rounded-xl border border-border bg-card px-4 py-3 shadow-sm">
          {fileUploads.map((upload, index) => (
            <div
              key={index}
              className="flex items-center gap-2 text-sm"
            >
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex items-center justify-between">
                  <span className="truncate text-foreground">{upload.file.name}</span>
                  <button
                    onClick={() => handleRemoveFile(upload.file)}
                    className="ml-2 p-1 text-muted-foreground transition-colors hover:text-foreground"
                    aria-label="Remove file"
                    disabled={upload.status === "uploading"}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                {upload.status === "uploading" && (
                  <div className="h-1.5 w-full rounded-full bg-muted">
                    <div
                      className="h-1.5 rounded-full bg-primary transition-all duration-300"
                      style={{ width: `${upload.progress}%` }}
                    />
                  </div>
                )}
                {upload.status === "success" && <div className="text-xs text-green-600">Uploaded successfully</div>}
                {upload.status === "error" && (
                  <div className="text-xs text-destructive">{upload.error ?? "Upload failed"}</div>
                )}
                {upload.status === "pending" && <div className="text-xs text-muted-foreground">Waiting to upload...</div>}
              </div>
              {upload.status === "uploading" && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
            </div>
          ))}
        </div>
      )}

      {/* Input bar — rounded rectangle that grows gracefully with content */}
      <div className="rounded-2xl border border-border bg-card px-4 py-3 shadow-md">
        <div className="relative flex items-end gap-3">
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            accept={CHAT_ACCEPT}
            className="hidden"
          />

          {/* Paperclip icon — far left */}
          <button
            onClick={handleFileInputClick}
            className="mb-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
            aria-label={attachHint}
            disabled={Boolean(disabled) || isUploading || !canAttach}
            title={attachHint}
          >
            <Paperclip className="h-4 w-4" />
          </button>

          {/* Message textarea - scales with user input */}
          <Textarea
            placeholder={placeholder}
            className="min-w-0 flex-1 border-0 bg-transparent py-1 shadow-none focus-visible:ring-0"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={Boolean(disabled) || isUploading}
            minHeight={48}
            maxHeight={240}
            rows={1}
          />

          {/* Send button */}
          <Button
            onClick={handleSendMessage}
            disabled={!canSend}
            className="mb-0.5 flex flex-shrink-0 items-center gap-2 rounded-xl bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
          >
            {isUploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Send
                <Send className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
